import { ChangeDetectionStrategy, Component, inject, ViewEncapsulation } from "@angular/core";
import { LayoutWidget } from "../layouts/se.layout.widget.abstract";
import { Layouts } from "../layouts/se.layouts";
import { S25IconComponent } from "../../s25-icon/s25.icon.component";
import { S25RouterLinkDirective } from "../../s25-nav-bar/s25.router.link.directive";
import { SEEventDetailsStore } from "./se.event.details.store";
import { SeOrgSecurityStore } from "../se.org.security.store";
import { SeAttendanceStore } from "../attendance/se.attendance.store";
import { S25States } from "../../../services/state/state.map";
import { Bind } from "../../../decorators/bind.decorator";
import { attendanceState, AttendanceStateName, UpdateAttendanceOptions } from "../attendance/attendance.service";
import { S25Icons } from "../../s25-icon/s25.icon.const";
import { SeOwnAttendanceStateComponent } from "../attendance/se.own.attendance.state.component";
import { LoginStore } from "../../../stores/login.store";

@Component({
    standalone: true,
    selector: "s25-ng-se-event-details-occurrences-widget",
    template: `
        @if (eventStore.occurrences()?.length) {
            <div class="occurrences-list">
                @for (occurrence of eventStore.occurrences(); track occurrence) {
                    <div class="occurrence-group">
                        <div class="occurrence-header">
                            <div class="occurrence-date">
                                @if (settingValues()["date-icon"]) {
                                    <s25-ng-icon
                                        [type]="asIconType(settingValues()['date-icon'])"
                                        [size]="20"
                                        [ariaLabel]="false"
                                        class="detail-icon"
                                    />
                                }
                                <span class="detail-value">{{ occurrence.date }}</span>
                            </div>
                            @if (loginStore.loggedIn()) {
                                <s25-ng-se-own-attendance-state
                                    [state]="occurrence.userAttendance?.attendeeState ?? 0"
                                    [eventId]="eventStore.eventId()"
                                    [occurrenceId]="occurrence.reservationId"
                                />
                            }
                        </div>
                        <div class="occurrence-body">
                            @if (occurrence.notes) {
                                <div class="occurrence-notes">
                                    {{ occurrence.notes }}
                                </div>
                            }
                            @if (occurrence.locations.length) {
                                <div class="occurrence-locations">
                                    @for (location of occurrence.locations; track location) {
                                        <div class="location-item">
                                            @if (settingValues()["location-icon"]) {
                                                <s25-ng-icon
                                                    [type]="asIconType(settingValues()['location-icon'])"
                                                    [size]="20"
                                                    class="detail-icon"
                                                    [ariaLabel]="false"
                                                />
                                            }
                                            <span class="detail-value">{{ location }}</span>
                                        </div>
                                    }
                                </div>
                            }

                            @if (orgSecStore.canManageAttendees() && !occurrence.hasEnded) {
                                <div class="checkin-button">
                                    <a
                                        s25-ng-router-link
                                        [stateName]="StateNames.studentEngagementEventCheckInDash"
                                        [stateParams]="{ rsrvId: occurrence.reservationId }"
                                        [inheritParams]="true"
                                    >
                                        Start Check-in
                                    </a>
                                </div>
                            }
                        </div>
                    </div>
                }
            </div>
        }
    `,
    styles: `
        .detail-icon {
            flex-shrink: 0;
            margin-top: 0.2rem;
            color: var(--cn-blue);
        }

        .detail-value {
            font-size: 1.125rem;
            color: var(--color--text);
            position: relative;
            top: 2px;
        }

        s25-ng-se-own-attendance-state {
            font-weight: normal;
        }

        /* Occurrences Section Styling */
        .occurrences-list {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
            gap: 1.5rem;
        }

        .occurrence-group {
            background: var(--color--surface-bg);
            padding: 0;
            border-radius: 12px;
            border: 1px solid var(--color--border);
            box-shadow:
                0 2px 4px rgba(0, 0, 0, 0.04),
                0 4px 8px rgba(0, 0, 0, 0.02);
            transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
            overflow: hidden;
            display: grid;
            grid-row: span 2;
            grid-template-rows: subgrid;
            row-gap: 0;
        }

        .occurrence-group:hover {
            transform: translateY(-2px);
            box-shadow:
                0 4px 12px rgba(0, 0, 0, 0.08),
                0 8px 16px rgba(0, 0, 0, 0.04);
            border-color: var(--cn-blue);
        }

        .occurrence-header {
            display: flex;
            align-items: flex-start;
            gap: 0.75rem;
            padding: 1rem 1.25rem;
            background: color-mix(in srgb, var(--cn-blue) 10%, var(--color--surface-bg));
            border-bottom: 1px solid color-mix(in srgb, var(--cn-blue) 15%, var(--color--surface-bg));
            font-weight: 600;
            /* navy (not --cn-blue): 4.5:1+ on the tinted header background */
            color: var(--cn-navy);
            justify-content: space-between;
        }

        .occurrence-body {
            display: grid;
            gap: 1.25rem;
            padding: 1.25rem;
        }

        .occurrence-date {
            display: flex;
            gap: 0.75rem;
        }

        .occurrence-date .detail-icon {
            color: var(--cn-navy);
        }

        .occurrence-date .detail-value {
            font-size: 1rem;
            color: var(--cn-navy);
        }

        .occurrence-notes {
            color: var(--color--text-muted);
            font-size: 0.95rem;
        }

        .occurrence-locations {
            display: flex;
            flex-direction: column;
            gap: 0.875rem;
        }

        .location-item {
            display: flex;
            align-items: flex-start;
            gap: 0.75rem;
            padding: 0.75rem 1rem;
            background: var(--color--surface-2-bg);
            border-radius: 8px;
            transition: all 0.2s ease;
            border: 1px solid transparent;
        }

        .location-item:hover {
            background: var(--color--surface-bg);
            border-color: var(--color--border);
            box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);
        }

        .location-indent {
            color: var(--cn-blue);
            flex-shrink: 0;
            margin-top: 2px;
        }

        .location-item .detail-value {
            color: var(--color--text);
            font-size: 0.95rem;
            line-height: 1.5;
        }

        .checkin-button {
            display: flex;
            justify-content: center;
        }

        @media (max-width: 768px) {
            .occurrences-list {
                grid-template-columns: 1fr;
                gap: 1rem;
            }

            .occurrence-header {
                flex-wrap: wrap;
                gap: 0.5rem;
                padding: 0.75rem 1rem;
            }

            .occurrence-body {
                gap: 1rem;
                padding: 1rem;
            }

            .location-item {
                padding: 0.5rem 0.75rem;
            }
        }
    `,
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [S25IconComponent, S25RouterLinkDirective, SeOwnAttendanceStateComponent],
})
export class SeEventDetailsOccurrencesWidgetComponent extends LayoutWidget<
    typeof SeEventDetailsOccurrencesWidgetComponent
> {
    static readonly META = {
        name: "Occurrences",
        info: "Lists each occurrence with date and locations",
        settings: [
            {
                id: "header",
                label: "Header",
                type: "fields",
                settings: [{ id: "date-icon", label: "Date Icon", type: "field", fieldType: "icon" }],
            },
            {
                id: "locations",
                label: "Locations",
                type: "sortable-collapses",
                settings: [{ id: "location-icon", label: "Icon", type: "field", fieldType: "icon" }],
            },
        ],
        defaultSettings: {
            "date-icon": "calendarDate",
            "location-icon": "locationMarker",
        },
    } as const satisfies Layouts.WidgetStatic;

    protected readonly eventStore = inject(SEEventDetailsStore);
    protected readonly orgSecStore = inject(SeOrgSecurityStore);
    protected readonly attendanceStore = inject(SeAttendanceStore);
    protected readonly loginStore = inject(LoginStore);
    protected readonly StateNames = S25States.names;

    @Bind
    setInterested(reservationId?: number) {
        return this.setState("INTERESTED", reservationId);
    }

    @Bind
    setNotInterested(reservationId?: number) {
        return this.setState("NOT_INTERESTED", reservationId);
    }

    async setState(state: AttendanceStateName, reservationId?: number) {
        const options: UpdateAttendanceOptions = { occurrences: [] };
        if (reservationId) options.occurrences!.push(reservationId);
        await this.attendanceStore.setAttendance(state, options);
    }

    asIconType(icon: string): S25Icons.Name {
        return <S25Icons.Name>icon;
    }

    protected readonly attendanceState = attendanceState;
}
